/* eslint-disable max-lines */
import {
  useLocationAlarmUpdates,
  useLocationById,
  useRspndrCancelIncidents,
  useRspndrIncidents,
  useRspndrNotification,
  useTenantDetails,
  useUpdateLocationAlarm,
} from '@hakimo-ui/hakimo/data-access';
import {
  IncidentRspndrType,
  Rspndr,
  ScanAlarmUpdateType,
  StatusType,
  Tenant,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { SelectMenu } from '@hakimo-ui/hakimo/ui-elements';
import { toast } from '@hakimo-ui/hakimo/util';
import { Alert, Button, Modal, Textarea } from '@hakimo-ui/shared/ui-base';
import {
  ChatBubbleLeftIcon,
  PaperAirplaneIcon,
  XCircleIcon,
} from '@heroicons/react/24/solid';
import { useState } from 'react';
import { useAlarmGroupStatic } from '../../unified-alarm-group/alarm-provider/AlarmGroupProvider';
import { generateHMAC, getCallList } from './util';

interface Props {
  locationId?: number;
  locationAlarmId?: string;
  locationTenantId?: string;
  timestamp?: string;
  toName: string;
  toNumber: string;
  escalationId?: string;
  product?: UnifiedMonitoringProduct;
}
export function ContactRspndr(props: Props) {
  const {
    locationAlarmId,
    locationId,
    locationTenantId,
    toName,
    toNumber,
    escalationId,
    product,
    timestamp,
  } = props;
  const isScanContext = product === UnifiedMonitoringProduct.SCAN;
  const incidentId = locationAlarmId;
  const { dataVersion } = useAlarmGroupStatic();

  const updatesQuery = useLocationAlarmUpdates(
    locationAlarmId ?? '',
    30000,
    timestamp ?? '',
    isScanContext ? 'scan' : 'remote-guarding',
    undefined,
    true,
    isScanContext ? undefined : dataVersion
  );
  const updateItems = updatesQuery.data?.items ?? [];
  const hasCancelEntry = updateItems.some((item) => {
    const combined = `${item.update_type} ${
      item.update_text ?? ''
    }`.toLowerCase();
    return combined.includes('rspndr') && combined.includes('cancel');
  });
  const hasFinishEntry = updateItems.some((item) => {
    const combined = `${item.update_type} ${
      item.update_text ?? ''
    }`.toLowerCase();
    return combined.includes('rspndr') && combined.includes('finish');
  });
  const hasEscalationEntry = isScanContext
    ? updateItems.some(
        (item) =>
          item.update_type === ScanAlarmUpdateType.RSPNDR && item.user !== null
      )
    : updateItems.some((item) => {
        const combined = `${item.update_type} ${
          item.update_text ?? ''
        }`.toLowerCase();
        return (
          combined.includes('rspndr') &&
          combined.includes('escalation') &&
          !combined.includes('cancel')
        );
      });

  const escalateDisabled =
    hasEscalationEntry || hasCancelEntry || hasFinishEntry;
  const cancelNotifyDisabled =
    !hasEscalationEntry || hasCancelEntry || hasFinishEntry;
  const [rspndrDetails, setRspndrDetails] = useState<Rspndr | null>(null);
  const [rspndrApiKey, setRspndrApiKey] = useState<string>('');
  const [rspndrApiSecret, setRspndrApiSecret] = useState<string>('');
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [rspndrUrl, setRspndrUrl] = useState<string | null>(null);
  const [showRspndrModal, setShowRspndrModal] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const [rspndrCancelUrl, setRspndrCancelUrl] = useState<string | null>(null);
  const [rspndrNotifyUrl, setRspndrNotifyUrl] = useState<string | null>(null);
  const [showRspndrCancelModal, setShowRspndrCancelModal] =
    useState<boolean>(false);
  const [showRspndrNotifyModal, setShowRspndrNotifyModal] =
    useState<boolean>(false);
  const [cancelNotes, setCancelNotes] = useState<string>('');
  const [notifyNotes, setNotifyNotes] = useState<string>('');
  const incidentTypes: IncidentRspndrType[] = [IncidentRspndrType.PATROL_CHECK];
  const [selectedIncidentType, setSelectedIncidentType] =
    useState<IncidentRspndrType>(incidentTypes[0]);

  const onClick = () => setShowRspndrModal(true);
  const onCloseCb = () => setShowRspndrModal(false);

  const onChangeCancel = (val: string) => {
    setCancelNotes(val);
  };

  const onChangeNotify = (val: string) => {
    setNotifyNotes(val);
  };

  const onChange = (val: string) => {
    setNotes(val);
  };

  const resolveAlarmMutation = useUpdateLocationAlarm(locationAlarmId ?? '', {
    dataVersion: isScanContext ? undefined : dataVersion,
  });
  const scanRspndrUpdateMutation = useUpdateLocationAlarm(
    locationAlarmId ?? '',
    {
      productType: 'scan',
    }
  );
  const locationDetails = useLocationById((locationId ?? '').toString()).data;

  const onSuccess = (tenant: Tenant) => {
    setRspndrApiKey(tenant.config?.rspndrApiKey || '');
    setRspndrApiSecret(tenant.config?.rspndrApiSecret || '');
    setTenantId(tenant.config?.rspndrTenantId || '');
    setRspndrUrl(tenant.config?.rspndrUrl || '');
    setRspndrCancelUrl(tenant.config?.rspndrCancelUrl || '');
    setRspndrNotifyUrl(tenant.config?.rspndrNotifyUrl || '');
    setRspndrDetails({
      escalationType: tenant.config?.escalationType || '',
      tenantId: tenant.config?.rspndrTenantId || '',
      locationId: locationId ?? 0,
      address1: locationDetails?.description || '',
      name: locationDetails?.name || '',
      city: locationDetails?.city || '',
      state: locationDetails?.state || '',
      country: locationDetails?.country || '',
      postal_code: tenant.config?.postalCode || '',
      incident_id: locationAlarmId,
      notes: notes,
      type: selectedIncidentType,
      call_list: getCallList(),
    });
  };

  useTenantDetails(locationTenantId || '', onSuccess);

  const handleRspndrIncidentSuccess = () => {
    toast(`Rspndr incidents created successfully!`, {
      type: 'success',
    });
    onCloseCb();
  };
  const handleRspndrIncidentError = () => {
    toast(`Rspndr incidents creation failed!`, {
      type: 'error',
    });
  };

  const handleRspndrCancelSuccess = () => {
    toast(`Successfully cancelled Rspndr Incident!`, {
      type: 'success',
    });
    onCloseCancel();
  };
  const handleRspndrCancelError = () => {
    toast(`Rspndr incident Cancelation failed!`, {
      type: 'error',
    });
  };
  const handleRspndrNotifySuccess = () => {
    toast(`Successfully sent Rspndr Notification!`, {
      type: 'success',
    });
    onCloseNotify();
  };
  const handleRspndrNotifyError = () => {
    toast(`Rspndr Notification failed!`, {
      type: 'error',
    });
  };

  const getApiToken = () => generateHMAC(rspndrApiKey, rspndrApiSecret);

  const mutation = useRspndrIncidents(
    handleRspndrIncidentSuccess,
    handleRspndrIncidentError,
    getApiToken,
    tenantId ? tenantId : '',
    rspndrUrl ? rspndrUrl : ''
  );

  const mutationCancel = useRspndrCancelIncidents(
    handleRspndrCancelSuccess,
    handleRspndrCancelError,
    getApiToken,
    tenantId ? tenantId : '',
    rspndrCancelUrl ? rspndrCancelUrl : ''
  );

  const mutationNotify = useRspndrNotification(
    handleRspndrNotifySuccess,
    handleRspndrNotifyError,
    getApiToken,
    tenantId ? tenantId : '',
    rspndrNotifyUrl ? rspndrNotifyUrl : ''
  );

  const resetCancelMutation = () => {
    if (mutationCancel.reset) {
      mutationCancel.reset();
    }
  };
  const resetNotifyMutation = () => {
    if (mutationNotify.reset) {
      mutationNotify.reset();
    }
  };

  const onClickCancel = () => {
    resetCancelMutation();
    setShowRspndrCancelModal(true);
  };
  const onCloseCancel = () => {
    resetCancelMutation();
    setShowRspndrCancelModal(false);
  };

  const onClickNotify = () => {
    resetNotifyMutation();
    setShowRspndrNotifyModal(true);
  };
  const onCloseNotify = () => {
    resetNotifyMutation();
    setShowRspndrNotifyModal(false);
  };

  const onRspndrIncidents = () => {
    if (rspndrDetails) {
      const updatedRspndrDetails: Rspndr = {
        ...rspndrDetails,
        notes: notes,
        call_list: getCallList(),
      };
      setRspndrDetails(updatedRspndrDetails as Rspndr);
      mutation.mutate(updatedRspndrDetails, {
        onSuccess: () => {
          if (isScanContext) {
            scanRspndrUpdateMutation.mutate({
              event_type: 'Rspndr',
              notes: notes,
              rspndr_details: {
                incident_type: selectedIncidentType,
                location_id: locationId,
                incident_id: escalationId,
              },
            } as unknown as Parameters<typeof scanRspndrUpdateMutation.mutate>[0]);
          } else {
            const resolveAlarmPayload = {
              status: StatusType.IN_PROGRESS,
              comment: `Escalation - RSPNDR incidents created successfully!`,
            };
            resolveAlarmMutation.mutate(resolveAlarmPayload);
          }
        },
        onError: () => {
          toast(`Rspndr incidents creation failed!`, {
            type: 'error',
          });
        },
      });
      setNotes('');
    } else {
      toast(`Rspndr details are not available!`, {
        type: 'error',
      });
      onCloseCb();
    }
  };

  const onRspndrCancelIncident = () => {
    const cancelBody = {
      incident_id: incidentId,
      notes: cancelNotes,
    };
    mutationCancel.mutate(cancelBody, {
      onSuccess: () => {
        if (isScanContext) {
          scanRspndrUpdateMutation.mutate({
            event_type: 'Rspndr',
            notes: cancelNotes,
            rspndr_details: {
              incident_type: selectedIncidentType,
              location_id: locationId,
              incident_id: incidentId,
            },
          } as unknown as Parameters<typeof scanRspndrUpdateMutation.mutate>[0]);
        } else {
          resolveAlarmMutation.mutate({
            comment: `Rspndr incident cancelled${
              cancelNotes ? `: ${cancelNotes}` : ''
            }`,
          });
        }
      },
    });
    setCancelNotes('');
  };

  const onRspndrNotifyIncident = () => {
    if (incidentId) {
      const notifyBody = {
        incident_id: incidentId,
        message: notifyNotes,
      };
      mutationNotify.mutate(notifyBody, {
        onSuccess: () => {
          if (isScanContext) {
            scanRspndrUpdateMutation.mutate({
              event_type: 'Rspndr',
              notes: notifyNotes,
              rspndr_details: {
                incident_type: selectedIncidentType,
                location_id: locationId,
                incident_id: escalationId,
              },
            } as unknown as Parameters<typeof scanRspndrUpdateMutation.mutate>[0]);
          } else {
            resolveAlarmMutation.mutate({
              comment: `Rspndr notification sent${
                notifyNotes ? `: ${notifyNotes}` : ''
              }`,
            });
          }
        },
        onError: () => {
          toast(`Rspndr notification failed!`, {
            type: 'error',
          });
        },
      });
      setNotifyNotes('');
    } else {
      toast(`Incident ID is not available!`, {
        type: 'error',
      });
      onCloseNotify();
    }
  };

  const renderActions = ({
    onSend,
    onClose,
    isLoading,
  }: {
    onSend: () => void;
    onClose: () => void;
    isLoading: boolean;
  }) => (
    <>
      <Button variant="primary" onClick={onSend} loading={isLoading}>
        Send
      </Button>
      <Button variant="outline" onClick={onClose} disabled={isLoading}>
        Close
      </Button>
    </>
  );

  return (
    <div>
      <div className="flex gap-2">
        <Button
          variant="icon"
          onClick={onClick}
          title="Rspndr Escalate"
          disabled={escalateDisabled}
        >
          <PaperAirplaneIcon className="text-primary-500 h-4 w-4" />
        </Button>
        <Button
          variant="icon"
          onClick={onClickCancel}
          title="Rspndr Cancel"
          disabled={cancelNotifyDisabled}
        >
          <XCircleIcon className="text-primary-500 h-4 w-4" />
        </Button>
        <Button
          variant="icon"
          onClick={onClickNotify}
          title="Rspndr Notify"
          disabled={cancelNotifyDisabled}
        >
          <ChatBubbleLeftIcon className="text-primary-500 h-4 w-4" />
        </Button>
      </div>
      {showRspndrModal && (
        <Modal
          open={true}
          onClose={onCloseCb}
          title="RSPNDR Escalate"
          footer={renderActions({
            onSend: onRspndrIncidents,
            onClose: onCloseCb,
            isLoading: mutation.isLoading,
          })}
        >
          <div className="max-w-xs p-4">
            <SelectMenu
              label="Choose an Incident Type"
              items={incidentTypes}
              value={selectedIncidentType}
              onChange={(val) => setSelectedIncidentType(val)}
              displayValue={(val) => val ?? ''}
              placeholder="Select a value"
            />
          </div>
          <div className="w-[30rem] pb-4 pl-4 pr-4">
            {mutation.isError && (
              <div className="px-8 pt-2">
                <Alert type="error">{mutation.error.message}</Alert>
              </div>
            )}
            <div className="mb-4">
              To: {toName} <span className="text-xs">{toNumber}</span>
            </div>
            <Textarea
              rows={3}
              value={notes}
              onChange={onChange}
              placeholder="Write your message here..."
            />
          </div>
        </Modal>
      )}
      {showRspndrCancelModal && (
        <Modal
          open={true}
          onClose={onCloseCancel}
          title="RSPNDR Cancel"
          footer={renderActions({
            onSend: onRspndrCancelIncident,
            onClose: onCloseCancel,
            isLoading: mutationCancel.isLoading,
          })}
        >
          <div className="w-[30rem] p-4">
            {mutationCancel.isError && (
              <div className="px-8 pt-2">
                <Alert type="error">{mutationCancel.error.message}</Alert>
              </div>
            )}
            <div className="mb-4">
              To: {toName} <span className="text-xs">{toNumber}</span>
            </div>
            <Textarea
              rows={3}
              value={cancelNotes}
              onChange={onChangeCancel}
              placeholder="Write your cancel message here..."
            />
          </div>
        </Modal>
      )}
      {showRspndrNotifyModal && (
        <Modal
          open={true}
          onClose={onCloseNotify}
          title="RSPNDR Notify"
          footer={renderActions({
            onSend: onRspndrNotifyIncident,
            onClose: onCloseNotify,
            isLoading: mutationNotify.isLoading,
          })}
        >
          <div className="w-[30rem] p-4">
            {mutationNotify.isError && (
              <div className="px-8 pt-2">
                <Alert type="error">{mutationNotify.error.message}</Alert>
              </div>
            )}
            <div className="mb-4">
              To: {toName} <span className="text-xs">{toNumber}</span>
            </div>
            <Textarea
              rows={3}
              value={notifyNotes}
              onChange={onChangeNotify}
              placeholder="Write your notify message here..."
            />
          </div>
        </Modal>
      )}
    </div>
  );
}
